import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { LEGAL_CONTACT_EMAIL, APP_NAME, LEGAL_LAST_UPDATED } from "@/lib/legal";

// Community Guidelines — public page linked from the feed and from
// the Privacy Policy. Required for Apple App Store 1.2 (user-generated content).

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 22 }}>
      <h3 style={{ color: "var(--text)", marginBottom: 8, fontSize: 15 }}>{title}</h3>
      <div style={{ color: "var(--text-dim)", fontSize: 13.5, lineHeight: 1.7 }}>
        {children}
      </div>
    </section>
  );
}

export default function CommunityGuidelinesScreen() {
  const navigate = useNavigate();
  const mailto = `mailto:${LEGAL_CONTACT_EMAIL}?subject=${encodeURIComponent(`${APP_NAME} — content report`)}`;

  return (
    <div className="screen fade-in" data-testid="community-guidelines-screen" style={{ paddingBottom: 60 }}>
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Back" data-testid="community-back">
          <ChevronLeft size={18} />
        </button>
        <div className="logo-mark">GUIDELINES</div>
        <span style={{ width: 32 }} />
      </div>

      <div className="eyebrow" style={{ marginTop: 16, marginBottom: 8 }}>
        COMMUNITY · UPDATED {LEGAL_LAST_UPDATED.toUpperCase()}
      </div>
      <h2 className="h1" style={{ fontSize: 28, marginBottom: 14, lineHeight: 1.15 }}>
        Nova Community Guidelines
      </h2>

      <p style={{ color: "var(--text-dim)", fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
        Nova&apos;s style feed exists so real people can share real progress,
        outfits, and self-improvement moments. To keep it welcoming, everyone
        agrees to a short set of rules before posting.
      </p>

      <Section title="What&apos;s allowed">
        <ul style={{ paddingLeft: 18, marginTop: 4 }}>
          <li>Photos of yourself — outfits, hair, grooming, fitness progress, meal shots.</li>
          <li>Honest captions, tips, and questions.</li>
          <li>Constructive feedback on other people&apos;s posts.</li>
          <li>Product mentions if you actually use the product.</li>
        </ul>
      </Section>

      <Section title="What&apos;s not allowed (zero tolerance)">
        <ul style={{ paddingLeft: 18, marginTop: 4 }}>
          <li><strong>Nudity, sexual content, or overtly suggestive imagery.</strong></li>
          <li><strong>Photos of anyone else without their consent</strong> — including friends, celebrities, or people in the background.</li>
          <li>Hate speech, slurs, harassment, bullying, or targeting individuals.</li>
          <li>Violence, gore, self-harm, or content that promotes eating disorders.</li>
          <li>Sale or promotion of illegal drugs, weapons, counterfeit goods, or stolen property.</li>
          <li>Spam, scams, misleading claims, or repetitive promotional content.</li>
          <li>Sharing anyone&apos;s personal information (address, phone, private photos).</li>
          <li>Impersonation of another person or brand.</li>
          <li>Illegal content of any kind under United States law.</li>
        </ul>
      </Section>

      <Section title="Medical and dietary claims">
        <p>
          You can share your own progress and what worked for you. Do <strong>not</strong>
          claim that your routine will cure, treat, or diagnose any medical condition.
          {APP_NAME} is a wellness app, not a clinic.
        </p>
      </Section>

      <Section title="How reporting works">
        <ul style={{ paddingLeft: 18, marginTop: 4 }}>
          <li>Every post has a three-dot menu with <em>Report</em> and <em>Block</em>.</li>
          <li>Reports are anonymous — the poster never knows who reported them.</li>
          <li>A post that receives 3 or more reports is automatically hidden pending review.</li>
          <li>Our team reviews reports within 24 hours and acts within 48 hours where a violation is confirmed.</li>
          <li>Blocking someone hides all of their posts and comments from your view immediately, and prevents them from following you or commenting on your posts.</li>
        </ul>
      </Section>

      <Section title="Consequences">
        <p>
          Violations may result in the post being removed, a warning, temporary
          suspension, or a permanent account ban. Repeat offenders and severe
          violations (illegal content, doxxing, sexual content) result in
          immediate permanent removal.
        </p>
      </Section>

      <Section title="How to contact us about content">
        <p>
          For urgent reports (illegal content, threats of harm, DMCA takedowns),
          email us directly at&nbsp;
          <a href={mailto} data-testid="community-guidelines-contact" style={{ color: "var(--gold)" }}>
            {LEGAL_CONTACT_EMAIL}
          </a>
          . Include the post URL or a screenshot and describe the issue.
        </p>
        <p style={{ marginTop: 8 }}>
          Standard reports go through the in-app <em>Report</em> button.
        </p>
      </Section>

      <div className="tiny" style={{ marginTop: 30, textAlign: "center", color: "var(--text-faint)", lineHeight: 1.55 }}>
        These guidelines are part of the Nova Terms of Service. Updated {LEGAL_LAST_UPDATED}.
      </div>
    </div>
  );
}
